import React from 'react';
import styled, {createGlobalStyle} from 'styled-components';
import {useIntl} from 'react-intl';
import {Button, tokensForThemeProvider} from '@forecast-it/design-system';
import {SaveState, isSaveDisabled} from '../shared/saveState';
import {SaveStatePill} from './SaveStatePill';
import {NovaStudioVariant, TASK_AGENT_VARIANT} from './novaStudioVariant';
import {NovaStudioColor} from '../shared/novaStudioColors';
import {NovaStudioAgentMenu} from './NovaStudioAgentMenu';

const HeaderRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	min-height: 72px;
	padding: 12px 24px;
	border-bottom: 1px solid ${NovaStudioColor.border};
`;
const tokens = tokensForThemeProvider.sky.sys;

// Set from the theme so the global h1 style does not apply; a long name is cut with an ellipsis rather
// than pushing the header actions onto a second line.
const AgentName = styled.h1`
	${tokens.font.heading['4']}
	color: ${tokens.color.text.content.main};
	margin: 0;
	min-width: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;
const TitleGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	min-width: 0;
`;
const HeaderActions = styled.div`
	display: flex;
	flex: none;
	align-items: center;
	gap: 16px;
`;

const SAVE_BUTTON_TEST_ID = 'nova-save';
const SAVE_AS_NEW_BUTTON_TEST_ID = 'nova-save-as-new';
const HISTORY_BUTTON_TEST_ID = 'nova-open-history';

// The Save button is intentionally never natively `disabled`: the guard that makes it a no-op
// while the draft is clean or a save is already in flight lives in the `onSave` handler the shell
// hands down (see NovaStudioShell.tsx), not here, so the same guard also protects the ⌘S/Ctrl+S
// shortcut. This restores a minimal equivalent look for its `aria-disabled` state, mirroring
// CreateAgentModal.tsx's submit button.
const SaveButtonAriaDisabledStyle = createGlobalStyle`
	[data-cy="${SAVE_BUTTON_TEST_ID}"][aria-disabled='true'],
	[data-cy="${SAVE_AS_NEW_BUTTON_TEST_ID}"][aria-disabled='true'] {
		opacity: 0.5;
		cursor: default;
	}
`;

interface Props {
	agentName: string | null;
	saveState: SaveState;
	// Whether the working draft has unsaved changes — combined with `saveState` to decide whether
	// the Save button is a no-op (see `isSaveDisabled`).
	isDirty: boolean;
	// Whether the selected agent's real content has actually finished loading into the draft (see
	// NovaStudioPage.tsx's detail-load gate) — combined with `isDirty`/`saveState` in
	// `isSaveDisabled` so Save can never fire while the draft still only holds empty/stale
	// placeholder values. Defaults to true so callers that don't exercise this gate (tests, and
	// any future caller with nothing to gate on) keep the old dirty/in-flight-only behavior.
	canSave?: boolean;
	// Triggers a save of the current working draft. Already guarded by the caller against a no-op
	// (clean draft) or a duplicate in-flight save, so this can be called unconditionally here.
	onSave: () => void;
	// Opens the version-history modal for the selected agent — owned by NovaStudioShell (which
	// has the agent's versions/currentVersionNumber and the restore handler), mirroring how
	// `onSave` is a plain, already-wired callback here rather than this component reaching for
	// Relay/modal state itself.
	onOpenHistory: () => void;
	// Copies the selected agent and moves focus to the copy. Owned by NovaStudioShell, which holds
	// the unsaved-changes guard and the catalogue selection this has to move.
	onDuplicateAgent: () => void;
	// Opens the delete confirmation for the selected agent. Nothing is destroyed by this alone.
	onDeleteAgent: () => void;
	// The save pill and version button wording, and whether the "…" menu (Duplicate, Delete) shows.
	variant?: NovaStudioVariant;
	// Saves the draft as the next version. Shown after Save when the variant shows version controls;
	// guarded by the caller like onSave.
	onSaveAsNew?: () => void;
	// Whether Save as new version can do anything right now.
	canSaveAsNew?: boolean;
}

export function NovaStudioHeader({
	agentName,
	saveState,
	isDirty,
	canSave = true,
	onSave,
	onOpenHistory,
	onDuplicateAgent,
	onDeleteAgent,
	variant = TASK_AGENT_VARIANT,
	onSaveAsNew,
	canSaveAsNew = false,
}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	const showsSaveAsNew = variant.showsVersionControls && onSaveAsNew !== undefined;

	return (
		<HeaderRow data-cy="nova-studio-header">
			<SaveButtonAriaDisabledStyle />
			{/* Placeholder copy — not localised yet; must go through the translations workflow. */}
			<TitleGroup>
				<AgentName>{agentName ?? 'No agent selected'}</AgentName>
			</TitleGroup>
			{agentName !== null && (
				<HeaderActions>
					<SaveStatePill saveState={saveState} variant={variant} />
					<Button data-cy={HISTORY_BUTTON_TEST_ID} emphasis="low" onPress={onOpenHistory}>
						{formatMessage({id: variant.versionButtonLabelId})}
					</Button>
					{variant.showAgentMenu && <NovaStudioAgentMenu onDuplicate={onDuplicateAgent} onDelete={onDeleteAgent} />}
					{/* With version controls, Save updates the version open and is the quieter action; Save as
					    new version follows it as the primary one. */}
					<Button
						data-cy={SAVE_BUTTON_TEST_ID}
						emphasis={showsSaveAsNew ? 'medium' : 'high'}
						aria-disabled={isSaveDisabled(isDirty, saveState, canSave)}
						onPress={onSave}
					>
						{formatMessage({id: 'nova.studio.saveButton'})}
					</Button>
					{showsSaveAsNew && (
						<Button
							data-cy={SAVE_AS_NEW_BUTTON_TEST_ID}
							emphasis="high"
							aria-disabled={!canSaveAsNew}
							onPress={onSaveAsNew}
						>
							{formatMessage({id: 'nova.studio.saveAsNewVersionButton'})}
						</Button>
					)}
				</HeaderActions>
			)}
		</HeaderRow>
	);
}
